import {
  AppShell as MantineAppShell,
  Badge,
  Burger,
  Button,
  Divider,
  Group,
  Image,
  NavLink,
  Text,
  Title,
} from '@mantine/core';
import { VersionLabel } from './version-label';
import { useDisclosure } from '@mantine/hooks';
import logoUrl from '../assets/sa-logo.png';
import {
  IconBell,
  IconBuildingCommunity,
  IconChartBar,
  IconClipboardList,
  IconHelpCircle,
  IconHierarchy,
  IconHistory,
  IconKey,
  IconLayoutList,
  IconLogout,
  IconPackage,
  IconSettings,
  IconTrash,
  IconUserExclamation,
  IconUserHeart,
  IconUserShield,
  IconUsers,
  type Icon,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMergeCandidatesCount } from '../api/patient-merge';
import { useLogout, useMe } from '../api/session';

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  disabled?: boolean;
  adminOnly?: boolean;
  // Restrict the item to super_admin operators only (e.g. patient
  // erasure). Stricter than `adminOnly`, which also shows for `admin`.
  superAdminOnly?: boolean;
  // When set, the nav item shows a red badge with the value when the
  // count is > 0. Used by Duplicate users → pending merge-candidate
  // count.
  badgeKind?: 'duplicates';
}

// Grouped so the navbar renders a visible divider between sections.
// Order within and across groups follows the operator-facing grouping:
//   1. day-to-day operational views (dashboard, cases)
//   2. configuration entities (products, orgs, question sets, workflows)
//   3. people + permissions (roles, users)
//   4. platform admin (admin users, audit log, settings)
const NAV_GROUPS: NavItem[][] = [
  [
    { to: '/', label: 'Dashboard', icon: IconChartBar },
    { to: '/patients', label: 'Patients', icon: IconUserHeart, adminOnly: true },
    { to: '/cases', label: 'Cases', icon: IconClipboardList },
  ],
  [
    { to: '/products', label: 'Products', icon: IconPackage, adminOnly: true },
    { to: '/orgs', label: 'Organisations', icon: IconBuildingCommunity },
    { to: '/question-sets', label: 'Question sets', icon: IconHelpCircle },
    { to: '/workflows', label: 'Workflows', icon: IconHierarchy },
  ],
  [
    { to: '/notifications/subscriptions', label: 'Subscriptions', icon: IconBell, adminOnly: true },
    { to: '/notifications/templates', label: 'Templates', icon: IconLayoutList, adminOnly: true },
  ],
  [
    { to: '/roles', label: 'Roles', icon: IconKey, adminOnly: true },
    { to: '/users', label: 'Users', icon: IconUsers, adminOnly: true },
    {
      to: '/users/duplicates',
      label: 'Duplicate users',
      icon: IconUserExclamation,
      adminOnly: true,
      badgeKind: 'duplicates',
    },
  ],
  [
    { to: '/admin-users', label: 'Admin users', icon: IconUserShield, adminOnly: true },
    { to: '/audit', label: 'Audit log', icon: IconHistory, adminOnly: true },
    {
      to: '/patient-erasure',
      label: 'Patient erasure',
      icon: IconTrash,
      superAdminOnly: true,
    },
    { to: '/settings', label: 'Settings', icon: IconSettings },
  ],
];

/**
 * Pick the single nav item that should render active for the current
 * pathname. Match the *most specific* (longest) target so a route
 * like `/users/duplicates` highlights "Duplicate users" rather than
 * also lighting up "Users" via prefix match.
 *
 * Dashboard (`/`) is exact-match only — otherwise it would light up
 * for every route.
 */
function pickActiveTarget(pathname: string, items: NavItem[]): string | null {
  let best: string | null = null;
  for (const { to } of items) {
    if (to === '/') {
      if (pathname === '/') best = to;
      continue;
    }
    if (pathname === to || pathname.startsWith(`${to}/`)) {
      if (!best || to.length > best.length) best = to;
    }
  }
  return best;
}

interface Props {
  children: ReactNode;
}

export function AppShell({ children }: Props) {
  const [opened, { toggle }] = useDisclosure();
  const { pathname } = useLocation();
  const me = useMe();
  const logout = useLogout();
  const duplicatesCount = useMergeCandidatesCount();

  return (
    <MantineAppShell
      header={{ height: 56 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <MantineAppShell.Header
        // Branded header. The logo's own background is the same dark
        // blue as `bg`, so the image sits flush against the header
        // with no visible seam. Text + icons get explicit white to
        // contrast against the dark background.
        style={{ background: '#110E4F', borderBottom: 'none' }}
      >
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" color="white" />
            <Image src={logoUrl} alt="Skin Analytics" h={40} w="auto" fit="contain" radius={0} />
            <Title order={4} c="white">
              Admin
            </Title>
          </Group>
          <Group gap="md" wrap="nowrap">
            <Text size="sm" c="white" visibleFrom="sm">
              {me.data?.email}
            </Text>
            <Button
              variant="subtle"
              color="white"
              size="compact-sm"
              leftSection={<IconLogout size={16} />}
              onClick={() => logout.mutate()}
              loading={logout.isPending}
              styles={{ root: { color: 'white' } }}
            >
              Sign out
            </Button>
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="md" style={{ display: 'flex', flexDirection: 'column' }}>
        <MantineAppShell.Section grow>
          {(() => {
            const role = me.data?.role;
            const visibleGroups = NAV_GROUPS.map((group) =>
              group.filter((item) => {
                if (item.superAdminOnly) return role === 'super_admin';
                if (item.adminOnly) return role === 'admin' || role === 'super_admin';
                return true;
              }),
            ).filter((group) => group.length > 0);
            const allVisible = visibleGroups.flat();
            const activeTarget = pickActiveTarget(pathname, allVisible);
            const dupCount = duplicatesCount.data?.count ?? 0;
            return visibleGroups.map((group, groupIdx) => (
              <div key={`group-${groupIdx}`}>
                {groupIdx > 0 && <Divider my="xs" />}
                {group.map((item) => {
                  const label = item.disabled ? `${item.label} (coming soon)` : item.label;
                  if (item.disabled) {
                    return (
                      <NavLink
                        key={item.to}
                        label={label}
                        leftSection={<item.icon size={18} />}
                        disabled
                      />
                    );
                  }
                  // Right-side badge for nav items that surface a count
                  // (currently just "Duplicate users"). Hidden when the
                  // count is zero or still loading so the navbar isn't
                  // cluttered with empty pills.
                  const badge =
                    item.badgeKind === 'duplicates' && dupCount > 0 ? (
                      <Badge color="red" variant="filled" size="sm" radius="sm">
                        {dupCount}
                      </Badge>
                    ) : null;
                  return (
                    <NavLink
                      key={item.to}
                      component={Link}
                      to={item.to}
                      label={label}
                      leftSection={<item.icon size={18} />}
                      rightSection={badge}
                      active={item.to === activeTarget}
                    />
                  );
                })}
              </div>
            ));
          })()}
        </MantineAppShell.Section>
        <MantineAppShell.Section>
          <Divider mb="xs" />
          <VersionLabel />
        </MantineAppShell.Section>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>{children}</MantineAppShell.Main>
    </MantineAppShell>
  );
}
